import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import Layout from 'ui/components/templates/Layout';
import { promoService } from 'ui/services';
import { convertSlugToNormalText, convertStringToSlug, formatDateToUTC } from 'ui/helpers/utils';
import useEntryPage from 'ui/hooks/useEntryPage';
import entryPageValues from 'ui/constants/entryPageValues';
import { showData } from 'ui/lib/mc';
import useIsNativeView from 'ui/hooks/useIsNativeView';
import getNativeRedirection from 'ui/helpers/utils/getNativeRedirection';
import { Article, BreadcrumbItems } from 'ui/interface';
import { IMetadata } from 'ui/components/atoms/Metadata/interface';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'ui/store';
import useGetBalanceAstrapay from 'ui/hooks/useGetBalanceAstrapay';
import useViewPromo from 'ui/hooks/emarsys/promo/useViewPromo';
import eventClickPromo from 'ui/helpers/emarsys/promo/eventClickPromo';
import { FormManagementType, FormManagementFormat } from 'ui/constants/formManagement';
import useFetchCmsForm from 'ui/hooks/useFetchCmsForm';
import useReadyInteractive from 'ui/hooks/useReadyInteractive';
import useSendGOPromo from 'ui/hooks/emarsys/promo/useSendGOPromo';
import useProduct from 'ui/hooks/useProduct';
import { dateFormatterEmarsys } from 'ui/helpers/utils/dateFormatter';
import { setPageBranch } from 'ui/store/googleTagManager/actions';

const PromoDetailPageTemplate = dynamic(
  () => import('ui/components/templates/PromoDetailPageTemplate'),
  { ssr: true }
);
const Metadata = dynamic(() => import('ui/components/atoms/Metadata'), { ssr: true });

interface PromoDetailPageProps {
  promo: Article;
  anotherPromo: Article[];
  metadata: IMetadata;
  errorCode: number;
  breadcrumbItems: BreadcrumbItems[];
}

const PromoDetailPage = ({
  promo,
  anotherPromo,
  metadata,
  errorCode,
  breadcrumbItems,
}: PromoDetailPageProps) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { interactive } = useReadyInteractive();

  const { saveEntryPage } = useEntryPage();

  const isNativeView = useIsNativeView();
  const { productList, getProductList } = useProduct();

  const category: string | string[] = router?.query?.kategori;
  const slug: string | string[] = router?.query?.slug;

  const { isAstrapayConnected } = useGetBalanceAstrapay();
  const { isLoyaltyProgramActive } = useSelector((state: IRootState) => state.loyalty);
  const { isLoggedIn, userData } = useSelector((state: IRootState) => state.auth);
  const isProfileCompleteLoyaltyCondition =
    userData?.isProfileCompleteLoyalty === true ||
    userData?.isProfileCompleteLoyalty === null ||
    userData?.isProfileCompleteLoyalty === undefined;
  const haveEarnedPoint = userData?.haveEarnPoint === false;
  const customerNoTdms = userData?.customerNoTDMS !== '';

  const showLoyaltyCard =
    category === 'lainnya' &&
    slug === process.env.NEXT_PUBLIC_LOYALTI_SLUG &&
    isLoyaltyProgramActive &&
    isProfileCompleteLoyaltyCondition &&
    haveEarnedPoint &&
    customerNoTdms &&
    (!isAstrapayConnected || !userData?.isProfileCompleteOnline);
  useEffect(() => {
    dispatch(setPageBranch('promo page'));
  }, []);
  useEffect(() => {
    showData({
      id: promo?.id,
      title: promo?.titlePage,
      category: promo?.contentCategory?.name,
      slug: promo?.slug,
      anotherCategory: category ? category : 'semua',
      baseSlug: router.basePath,
    });

    if (!errorCode) {
      sessionStorage.removeItem('entryPage');
      saveEntryPage(entryPageValues.PROMO_DETAIL_PAGE);
    }
  }, [promo, category, errorCode, saveEntryPage, router.basePath]);

  useEffect(() => {
    const promoSlug = promo?.slug;

    if (productList.length === 0) {
      if (promoSlug) {
        getProductList(promoSlug);
      } else {
        getProductList();
      }
    }
  }, [promo]);

  useViewPromo({
    pageName: 'promo detail page',
    pageTitle: promo?.titlePage,
    breadcrumbItems,
    newParams: {
      ...(breadcrumbItems?.[2]?.name && { menu_cat_lvl2: breadcrumbItems[2].name.toLowerCase() }),
      ...(breadcrumbItems?.[3]?.name && { menu_cat_lvl3: breadcrumbItems[3].name.toLowerCase() }),
      promo_periode: `${dateFormatterEmarsys(promo?.startDate)} - ${dateFormatterEmarsys(promo?.endDate)}`,
      ...(promo?.model?.[0] && { car_model_1: promo.model[0] }),
      ...(promo?.model?.[1] && { car_model_2: promo.model[1] }),
      ...(promo?.model?.[2] && { car_model_3: promo.model[2] }),
      ...(promo?.contentCategory?.name && {
        page_category: promo.contentCategory.name.toLowerCase(),
      }),
    },
  });

  useSendGOPromo();

  useFetchCmsForm(router.query?.src, FormManagementType.PROMO, FormManagementFormat.FORM_TAMBAHAN);
  useFetchCmsForm(router.query?.src, FormManagementType.PROMO, FormManagementFormat.POPUP);

  return (
    <Layout isNativeView={isNativeView} showTasia={false} withRecaptchaV3={interactive}>
      <Metadata {...metadata} />

      <PromoDetailPageTemplate
        promo={promo}
        anotherPromo={anotherPromo}
        errorCode={errorCode}
        isNativeView={isNativeView}
        breadcrumbItems={breadcrumbItems}
        showLoyaltyCard={showLoyaltyCard}
        actionClick={(buttonName: string, sectionName: string) =>
          eventClickPromo(
            dispatch,
            promo,
            breadcrumbItems,
            userData?.customerNo,
            buttonName,
            sectionName
          )
        }
        feature="PROMO_DETAIL"
        pageTitle={metadata?.title || ''}
      />

      {/*       
        This tag is specifically used for WhatsApp live chat integration. 
        It will remain hidden from the UI (not visible to users). 
        The Backend (BE) will extract the product value from this tag 
        by reading the className `emarsys-product-name`.  
      */}
      {productList?.length === 1 && (
        <div className="hidden emarsys-product-name">{productList[0]?.name}</div>
      )}
    </Layout>
  );
};

export async function getServerSideProps(context) {
  const slug = context.params.slug;
  const category = context.params.kategori;

  const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;
  const basePathUrl = process.env.NEXT_PUBLIC_BASEPATH_URL;

  context.res.setHeader('Cache-Control', 'public, max-age=3600');

  // Redirect if URL is not lowercase
  if (category !== category.toLowerCase()) {
    return {
      redirect: {
        destination: `/${category.split(' ').join('-').toLowerCase()}/${slug}`,
        statusCode: 301,
      },
    };
  }

  // Handle redirection for native view
  const { isRedirecting, redirectUrl } = getNativeRedirection(context, ['kategori', 'slug']);
  if (isRedirecting) {
    return {
      redirect: {
        destination: redirectUrl,
        statusCode: 301,
      },
    };
  }

  try {
    const promoDetailPromise = promoService.getPromoBySlug(slug, true);
    const anotherPromoPromise = promoService.getAnotherPromo(true);

    const [promoDetailResult, anotherPromoResult] = await Promise.all([
      promoDetailPromise,
      anotherPromoPromise,
    ]);

    const promo = promoDetailResult.data;
    const anotherPromo = anotherPromoResult.data.content || [];

    const INVALID_ARTICLE_TYPES = ['salesman', 'branch'];

    if (
      INVALID_ARTICLE_TYPES.includes(promo?.articleType) ||
      promo?.status?.toLowerCase() !== 'publish'
    ) {
      return {
        notFound: true,
      };
    }

    // Format  publish and modified date
    const publishedDate = formatDateToUTC(promo.publishedDate);
    const modifiedDate = formatDateToUTC(promo.modifiedDate);
    const robotsTag = promo?.metaRobots?.map((robots) => robots?.code);

    const metadata = {
      title: `${promo.titleHeader} | Auto2000`,
      description: promo?.metaDescription,
      keywords: promo?.keyword,
      ogImage: promo?.heroImageLink,
      author: promo?.authorName ? promo?.authorName : 'Auto2000',
      slug: promo?.slug,
      robots: process.env.ENV === 'PRD' && !context?.query?.src ? robotsTag : 'noindex, nofollow',
      publishedDate,
      modifiedDate,
    };

    const validCategory =
      category !== convertStringToSlug(promo?.contentCategory?.name)
        ? convertStringToSlug(promo?.contentCategory?.name)
        : category;

    const breadcrumbItems = [
      {
        name: 'Home',
        item: `${PUBLIC_URL}`,
        path: `${PUBLIC_URL}`,
      },
      {
        name: 'Promo',
        item: `${basePathUrl}`,
        path: `${basePathUrl}`,
      },
      {
        name: convertSlugToNormalText(validCategory),
        item: `${basePathUrl}/${validCategory}`,
        path: `${basePathUrl}/${validCategory}`,
      },
      {
        name: promo?.titlePage,
      },
    ];

    return {
      props: {
        promo,
        anotherPromo,
        metadata,
        breadcrumbItems,
      },
    };
  } catch (err) {
    console.log({ err });

    if (err.response?.data?.status === 404) {
      return {
        notFound: true,
      };
    }

    context.res.setHeader('Retry-After', '3600');
    context.res.statusCode = 503;
    return {
      props: {
        errorCode: err.response?.data?.status || 500,
      },
    };
  }
}

export default PromoDetailPage;
